import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FiatPaymentMethod, TransactionError, TransactionType, Utils, Validations, useAsset, useAssetContext, useAuthContext, useBuy, useFiat, useSessionContext, useUserContext, } from '@dfx.swiss/react';
import { Form, SpinnerSize, StyledButton, StyledButtonColor, StyledButtonWidth, StyledDropdown, StyledHorizontalStack, StyledInput, StyledLink, StyledLoadingSpinner, StyledSearchDropdown, StyledTextBox, StyledVerticalStack, } from '@dfx.swiss/react-components';
import { AssetCategory } from '@dfx.swiss/react/dist/definitions/asset';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { NameEdit } from '../components/edit/name.edit';
import { ErrorHint } from '../components/error-hint';
import { ExchangeRate } from '../components/exchange-rate';
import { KycHint } from '../components/kyc-hint';
import { Layout } from '../components/layout';
import { AddressSwitch } from '../components/payment/address-switch';
import { BuyCompletion } from '../components/payment/buy-completion';
import { PaymentInformationContent } from '../components/payment/payment-information';
import { PrivateAssetHint } from '../components/private-asset-hint';
import { SanctionHint } from '../components/sanction-hint';
import { PaymentMethodDescriptions, PaymentMethodLabels } from '../config/labels';
import { useAppHandlingContext } from '../contexts/app-handling.context';
import { useSettingsContext } from '../contexts/settings.context';
import { useWalletContext } from '../contexts/wallet.context';
import { useAppParams } from '../hooks/app-params.hook';
import { useBlockchain } from '../hooks/blockchain.hook';
import useDebounce from '../hooks/debounce.hook';
import { useAddressGuard } from '../hooks/guard.hook';
import { useNavigation } from '../hooks/navigation.hook';
import { blankedAddress } from '../util/utils';
const EmbeddedWallet = 'CakeWallet';
export function BuyScreen() {
    var _a;
    useAddressGuard();
    const { translate, translateError } = useSettingsContext();
    const { logout } = useSessionContext();
    const { session } = useAuthContext();
    const { currencies, receiveFor } = useBuy();
    const { toSymbol } = useFiat();
    const { assets, getAssets } = useAssetContext();
    const { getAsset, isSameAsset } = useAsset();
    const { assets: assetFilter, assetIn, assetOut, amountIn, blockchain, paymentMethod, externalTransactionId, wallet, flags, setParams, availableBlockchains, } = useAppParams();
    const { toDescription, getCurrency, getDefaultCurrency } = useFiat();
    const { navigate } = useNavigation();
    const { user } = useUserContext();
    const { blockchain: walletBlockchain, switchBlockchain } = useWalletContext();
    const scrollRef = useRef(null);
    const rootRef = useRef(null);
    const { toString } = useBlockchain();
    const { isEmbedded, isDfxHosted, isInitialized } = useAppHandlingContext();
    const [availableAssets, setAvailableAssets] = useState();
    const [paymentInfo, setPaymentInfo] = useState();
    const [customAmountError, setCustomAmountError] = useState();
    const [errorMessage, setErrorMessage] = useState();
    const [kycError, setKycError] = useState();
    const [showsCompletion, setShowsCompletion] = useState(false);
    const [showsSwitchScreen, setShowsSwitchScreen] = useState(false);
    const [showsNameForm, setShowsNameForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPriceLoading, setIsPriceLoading] = useState(false);
    const [isContinue, setIsContinue] = useState(false);
    const [validatedData, setValidatedData] = useState();
    // form
    const { control, handleSubmit, setValue, resetField } = useForm({
        defaultValues: {
            amount: '100',
        },
    });
    const selectedAmount = useWatch({ control, name: 'amount' });
    const selectedCurrency = useWatch({ control, name: 'currency' });
    const selectedAsset = useWatch({ control, name: 'asset' });
    const selectedPaymentMethod = useWatch({ control, name: 'paymentMethod' });
    const selectedAddress = useWatch({ control, name: 'address' });
    function setVal(field, value) {
        setValue(field, value, { shouldValidate: true });
    }
    const filteredAssets = assets && filterAssets(Array.from(assets.values()).flat(), assetFilter);
    const blockchains = availableBlockchains === null || availableBlockchains === void 0 ? void 0 : availableBlockchains.filter((b) => filteredAssets === null || filteredAssets === void 0 ? void 0 : filteredAssets.some((a) => a.blockchain === b));
    const addressItems = (session === null || session === void 0 ? void 0 : session.address) && (blockchains === null || blockchains === void 0 ? void 0 : blockchains.length)
        ? [
            ...blockchains.map((b) => {
                var _a;
                return ({
                    address: blankedAddress((_a = session.address) !== null && _a !== void 0 ? _a : ''),
                    label: toString(b),
                    chain: b,
                });
            }),
            {
                address: translate('screens/buy', 'Switch address'),
                label: translate('screens/buy', 'Login with a different address'),
            },
        ]
        : [];
    const availablePaymentMethods = [FiatPaymentMethod.BANK];
    (!selectedAsset || selectedAsset.instantBuyable) && availablePaymentMethods.push(FiatPaymentMethod.INSTANT);
    (isDfxHosted || !isEmbedded) &&
        wallet !== EmbeddedWallet &&
        ((_a = user === null || user === void 0 ? void 0 : user.activeAddress) === null || _a === void 0 ? void 0 : _a.wallet) !== EmbeddedWallet &&
        (!selectedAsset || (selectedAsset === null || selectedAsset === void 0 ? void 0 : selectedAsset.cardBuyable)) &&
        availablePaymentMethods.push(FiatPaymentMethod.CARD);
    const availableCurrencies = currencies === null || currencies === void 0 ? void 0 : currencies.filter((c) => selectedPaymentMethod === FiatPaymentMethod.CARD
        ? c.cardSellable
        : selectedPaymentMethod === FiatPaymentMethod.INSTANT
            ? c.instantSellable
            : c.sellable);
    useEffect(() => {
        var _a;
        const activeBlockchain = walletBlockchain !== null && walletBlockchain !== void 0 ? walletBlockchain : blockchain;
        const activeBlockchains = activeBlockchain ? [activeBlockchain] : availableBlockchains !== null && availableBlockchains !== void 0 ? availableBlockchains : [];
        const blockchainAssets = getAssets(activeBlockchains, { buyable: true, comingSoon: false }).filter((a) => a.category === AssetCategory.PUBLIC || a.name === assetOut);
        const activeAssets = filterAssets(blockchainAssets, assetFilter);
        setAvailableAssets(activeAssets);
        const asset = (_a = getAsset(activeAssets, assetOut)) !== null && _a !== void 0 ? _a : (activeAssets.length === 1 && activeAssets[0]);
        if (asset)
            setVal('asset', asset);
    }, [assetOut, assetFilter, getAsset, getAssets, blockchain, walletBlockchain, availableBlockchains]);
    useEffect(() => {
        var _a, _b;
        const currency = (_b = (_a = getCurrency(availableCurrencies, selectedCurrency === null || selectedCurrency === void 0 ? void 0 : selectedCurrency.name)) !== null && _a !== void 0 ? _a : getCurrency(availableCurrencies, assetIn)) !== null && _b !== void 0 ? _b : getDefaultCurrency(availableCurrencies);
        if (currency)
            setVal('currency', currency);
    }, [assetIn, getCurrency, currencies, selectedPaymentMethod]);
    useEffect(() => {
        var _a, _b;
        const selectedMethod = (_b = (_a = availablePaymentMethods.find((m) => m === selectedPaymentMethod)) !== null && _a !== void 0 ? _a : availablePaymentMethods.find((m) => m.toLowerCase() === (paymentMethod === null || paymentMethod === void 0 ? void 0 : paymentMethod.toLowerCase()))) !== null && _b !== void 0 ? _b : FiatPaymentMethod.BANK;
        if (isInitialized && selectedMethod)
            setVal('paymentMethod', selectedMethod);
    }, [availablePaymentMethods, paymentMethod]);
    useEffect(() => {
        if (amountIn)
            setVal('amount', amountIn);
    }, [amountIn]);
    useEffect(() => setAddress(), [session === null || session === void 0 ? void 0 : session.address, translate]);
    useEffect(() => {
        if (selectedAddress) {
            if (selectedAddress.chain) {
                if (blockchain !== selectedAddress.chain) {
                    setParams({ blockchain: selectedAddress.chain });
                    switchBlockchain(selectedAddress.chain);
                    resetField('asset');
                    setAvailableAssets(undefined);
                }
            }
            else {
                setShowsSwitchScreen(true);
                setAddress();
            }
        }
    }, [selectedAddress]);
    useEffect(() => {
        if (!selectedAmount) {
            setCustomAmountError(undefined);
        }
    }, [selectedAmount]);
    // data validation/fetch
    useEffect(() => updateData(), [selectedAmount, selectedCurrency, selectedAsset, selectedPaymentMethod]);
    function updateData() {
        const data = validateData({
            amount: selectedAmount,
            currency: selectedCurrency,
            asset: selectedAsset,
            paymentMethod: selectedPaymentMethod,
        });
        setValidatedData(data);
    }
    useEffect(() => {
        let isRunning = true;
        setErrorMessage(undefined);
        if (!validatedData) {
            setPaymentInfo(undefined);
            setIsLoading(false);
            setIsPriceLoading(false);
            return;
        }
        const data = Object.assign(Object.assign({}, validatedData), { externalTransactionId });
        setIsLoading(true);
        receiveFor(data)
            .then((buy) => {
            if (isRunning) {
                validateBuy(buy);
                setPaymentInfo(buy);
                // load exact price
                if (buy && !buy.exactPrice) {
                    setIsPriceLoading(true);
                    receiveFor(Object.assign(Object.assign({}, data), { exactPrice: true }))
                        .then((info) => {
                        if (isRunning) {
                            setPaymentInfo(info);
                            setIsPriceLoading(false);
                        }
                    })
                        .catch(console.error);
                }
            }
        })
            .catch((error) => {
            var _a;
            if (isRunning) {
                setPaymentInfo(undefined);
                setErrorMessage((_a = error.message) !== null && _a !== void 0 ? _a : 'Unknown error');
            }
        })
            .finally(() => isRunning && setIsLoading(false));
        return () => {
            isRunning = false;
        };
    }, [useDebounce(validatedData, 500)]);
    function validateBuy(buy) {
        setCustomAmountError(undefined);
        setKycError(undefined);
        switch (buy.error) {
            case TransactionError.AMOUNT_TOO_LOW:
                setCustomAmountError(translate('screens/payment', 'Entered amount is below minimum deposit of {{amount}} {{currency}}', {
                    amount: Utils.formatAmount(buy.minVolume),
                    currency: buy.currency.name,
                }));
                return;
            case TransactionError.AMOUNT_TOO_HIGH:
                setCustomAmountError(translate('screens/payment', 'Entered amount is above maximum deposit of {{amount}} {{currency}}', {
                    amount: Utils.formatAmount(buy.maxVolume),
                    currency: buy.currency.name,
                }));
                return;
            case TransactionError.LIMIT_EXCEEDED:
            case TransactionError.KYC_REQUIRED:
            case TransactionError.KYC_DATA_REQUIRED:
            case TransactionError.KYC_REQUIRED_INSTANT:
            case TransactionError.BANK_TRANSACTION_MISSING:
                setKycError(buy.error);
                return;
        }
    }
    function validateData({ amount: amountStr, currency, asset, paymentMethod } = {}) {
        const amount = Number(amountStr);
        if (amount > 0 && asset != null && currency != null && paymentMethod != null) {
            return { amount, currency, asset, paymentMethod };
        }
    }
    // misc
    function filterAssets(assets, filter) {
        if (!filter)
            return assets;
        const allowedAssets = filter.split(',');
        return assets.filter((a) => allowedAssets.some((f) => isSameAsset(a, f)));
    }
    function onSubmit(_data) {
        // TODO: (Krysh fix broken form validation and onSubmit
    }
    function setAddress() {
        var _a;
        if (isInitialized && (session === null || session === void 0 ? void 0 : session.address) && addressItems) {
            const address = (_a = addressItems.find((a) => blockchain && a.chain === blockchain)) !== null && _a !== void 0 ? _a : addressItems[0];
            setVal('address', address);
        }
    }
    function onAddressSwitch() {
        logout();
        navigate('/connect', { setRedirect: true });
    }
    function onCardBuy(info) {
        if (info.nameRequired) {
            setShowsNameForm(true);
        }
        else {
            openPaymentLink();
        }
    }
    function openPaymentLink() {
        if (!(paymentInfo === null || paymentInfo === void 0 ? void 0 : paymentInfo.paymentLink))
            return;
        setIsContinue(true);
        window.location.href = paymentInfo.paymentLink;
    }
    const rules = Utils.createRules({
        asset: Validations.Required,
        currency: Validations.Required,
        amount: Validations.Required,
    });
    const title = showsCompletion
        ? translate('screens/buy', 'Done!')
        : showsSwitchScreen
            ? translate('screens/buy', 'Switch address')
            : translate('screens/buy', 'Buy');
    return (_jsx(Layout, Object.assign({ title: title, backButton: !showsCompletion, onBack: showsNameForm ? () => setShowsNameForm(false) : undefined, textStart: true, rootRef: rootRef, scrollRef: scrollRef }, { children: showsSwitchScreen ? (_jsx(AddressSwitch, { onClose: (r) => (r ? onAddressSwitch() : setShowsSwitchScreen(false)) })) : showsCompletion && paymentInfo ? (_jsx(BuyCompletion, { user: user, paymentInfo: paymentInfo, navigateOnClose: true })) : showsNameForm ? (_jsx(NameEdit, { onSuccess: openPaymentLink })) : (_jsx(Form, Object.assign({ control: control, rules: rules, errors: {}, onSubmit: handleSubmit(onSubmit), translate: translateError }, { children: _jsx(StyledVerticalStack, Object.assign({ gap: 8, full: true, center: true }, { children: availableCurrencies && availableAssets && (_jsxs(_Fragment, { children: [_jsxs(StyledVerticalStack, Object.assign({ gap: 2, full: true }, { children: [_jsx("h2", Object.assign({ className: "text-dfxGray-700" }, { children: translate('screens/buy', 'You spend') })), _jsxs(StyledHorizontalStack, Object.assign({ gap: 1 }, { children: [_jsx("div", Object.assign({ className: "flex-[3_1_9rem]" }, { children: _jsx(StyledInput, { type: "number", placeholder: "0.00", prefix: selectedCurrency && toSymbol(selectedCurrency), name: "amount", forceError: customAmountError != null, forceErrorMessage: customAmountError, full: true }) })), _jsx("div", Object.assign({ className: "flex-[1_0_9rem]" }, { children: _jsx(StyledDropdown, { rootRef: rootRef, name: "currency", placeholder: translate('general/actions', 'Select...'), items: availableCurrencies, labelFunc: (item) => item.name, descriptionFunc: (item) => toDescription(item), full: true }) }))] })), _jsx(StyledDropdown, { rootRef: rootRef, name: "paymentMethod", placeholder: translate('general/actions', 'Select...'), items: availablePaymentMethods, labelFunc: (item) => translate('screens/payment', PaymentMethodLabels[item]), descriptionFunc: (item) => translate('screens/payment', PaymentMethodDescriptions[item]), full: true })] })), _jsxs(StyledVerticalStack, Object.assign({ gap: 2, full: true }, { children: [_jsx("h2", Object.assign({ className: "text-dfxGray-700" }, { children: translate('screens/buy', 'You get about') })), _jsxs(StyledHorizontalStack, Object.assign({ gap: 1 }, { children: [_jsx("div", Object.assign({ className: "flex-[3_1_9rem]" }, { children: _jsx(StyledTextBox, { text: paymentInfo && !isLoading ? `≈ ${Utils.formatAmountCrypto(paymentInfo.estimatedAmount)}` : ' ', loading: !isLoading && isPriceLoading, full: true }) })), _jsx("div", Object.assign({ className: "flex-[1_0_9rem]" }, { children: _jsx(StyledSearchDropdown, { rootRef: rootRef, name: "asset", placeholder: translate('general/actions', 'Select...'), items: availableAssets, labelFunc: (item) => item.name, assetIconFunc: (item) => item.name, descriptionFunc: (item) => item.description, filterFunc: (item, search) => !search || item.name.toLowerCase().includes(search.toLowerCase()), full: true }) }))] })), _jsx(StyledDropdown, { rootRef: rootRef, name: "address", items: addressItems, labelFunc: (item) => item.address, descriptionFunc: (item) => item.label, full: true, forceEnable: true })] })), isLoading ? (_jsx(StyledVerticalStack, Object.assign({ center: true }, { children: _jsx(StyledLoadingSpinner, { size: SpinnerSize.LG }) }))) : (_jsxs(_Fragment, { children: [kycError && _jsx(KycHint, { type: TransactionType.BUY, error: kycError }), errorMessage && (_jsxs(StyledVerticalStack, Object.assign({ center: true, className: "text-center" }, { children: [_jsx(ErrorHint, { message: errorMessage }), _jsx(StyledButton, { width: StyledButtonWidth.MIN, label: translate('general/actions', 'Retry'), onClick: updateData, className: "mt-4", color: StyledButtonColor.STURDY_WHITE })] }))), paymentInfo &&
                                    !kycError &&
                                    !errorMessage &&
                                    !customAmountError &&
                                    ((selectedAsset === null || selectedAsset === void 0 ? void 0 : selectedAsset.category) === AssetCategory.PRIVATE && !(flags === null || flags === void 0 ? void 0 : flags.includes('private')) ? (_jsx(PrivateAssetHint, { asset: selectedAsset })) : (_jsxs(_Fragment, { children: [_jsx(ExchangeRate, { exchangeRate: paymentInfo.exchangeRate, rate: paymentInfo.rate, fees: paymentInfo.fees, feeCurrency: paymentInfo.currency, from: paymentInfo.currency, to: paymentInfo.asset }), selectedPaymentMethod !== FiatPaymentMethod.CARD ? (_jsxs(_Fragment, { children: [_jsx("div", { children: _jsx(PaymentInformationContent, { info: paymentInfo }) }), _jsx(SanctionHint, {}), _jsxs("div", Object.assign({ className: "w-full leading-none" }, { children: [_jsx(StyledLink, { label: translate('screens/payment', 'Please note that by using this service you automatically accept our terms and conditions. The effective exchange rate is fixed when the money is received and processed by DFX.'), url: process.env.REACT_APP_TNC_URL, small: true, dark: true }), _jsx(StyledButton, { width: StyledButtonWidth.FULL, label: translate('screens/buy', 'Click here once you have issued the transfer'), onClick: () => {
                                                                    var _a;
                                                                    setShowsCompletion(true);
                                                                    (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(0, 0);
                                                                }, caps: false, className: "mt-4" })] }))] })) : (paymentInfo.paymentLink && (_jsxs(_Fragment, { children: [_jsx(SanctionHint, {}), _jsxs("div", Object.assign({ className: "leading-none" }, { children: [_jsx(StyledLink, { label: translate('screens/payment', 'Please note that by using this service you automatically accept our terms and conditions and authorize DFX.swiss to collect the above amount via your chosen payment method and agree that this amount cannot be canceled, recalled or refunded.'), url: process.env.REACT_APP_TNC_URL, small: true, dark: true }), _jsx(StyledButton, { width: StyledButtonWidth.FULL, label: translate('general/actions', 'Next'), onClick: () => onCardBuy(paymentInfo), isLoading: isContinue, className: "mt-4" })] }))] })))] })))] }))] })) })) }))) })));
}
